import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import VendorTeamDepartureSchema, {
  VendorTeamDepartureJSON,
  VendorTeamDepartureParams,
  VendorTeamDepartureReason,
} from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-departure-schema";

export { VendorTeamDepartureReason };
export type { VendorTeamDepartureJSON, VendorTeamDepartureParams };

/**
 * The status a screen shows beside a person who has left a work agreement's vendor team.
 *
 * Two different events used to read the same way once the person was off the roster (#5663): a member
 * who takes themselves off, and a member a team leader takes off. These are the words that tell them
 * apart, and they are the whole reason the reason is carried at all.
 *
 * Short on purpose. They are drawn in the roster's Role column, which is 110px wide so that all four
 * of a roster's columns fit the narrowest surface a team is read on. "Removed from work agreement
 * team", the wording #5663 asks for, came out of that column as "Removed from work agre..." -- a
 * status a reader cannot read is not a status shown. Each one leads with its own verb, so the two are
 * still different at a glance even where the column is narrower still, and the cell carries the whole
 * wording as its tooltip.
 */
export const VENDOR_TEAM_DEPARTURE_STATUS: Readonly<Record<VendorTeamDepartureReason, string>> = {
  [VendorTeamDepartureReason.Left]: "Left the team",
  [VendorTeamDepartureReason.Removed]: "Removed from team",
};

/**
 * The one word a screen draws beside a departed person's name where even the status will not fit: a
 * fee schedule's Team Member cell (#5811). Each is the status's own leading verb, so the two surfaces
 * agree, and the cell carries the whole status as its tooltip.
 */
export const VENDOR_TEAM_DEPARTURE_LABEL: Readonly<Record<VendorTeamDepartureReason, string>> = {
  [VendorTeamDepartureReason.Left]: "Left",
  [VendorTeamDepartureReason.Removed]: "Removed",
};

/**
 * One person's exit from a work agreement's vendor team: who left, why, and when.
 *
 * Read-only on this side. The server writes a departure when the roster changes and drops it again if
 * the same person rejoins, so a person is never both on the roster and in this list.
 */
export default class VendorTeamDeparture {
  public static get EXAMPLE(): VendorTeamDeparture {
    return new VendorTeamDeparture({
      userId: Guid.generate(),
      legalEntityId: Guid.generate(),
      reason: VendorTeamDepartureReason.Left,
      departedDate: new AHDate(),
    });
  }

  public static create(params: VendorTeamDepartureParams): VendorTeamDeparture {
    return new VendorTeamDeparture(VendorTeamDepartureSchema.validate(params));
  }

  public static fromJSON(json: VendorTeamDepartureJSON | null | undefined): VendorTeamDeparture | undefined {
    if (isNullOrUndefined(json)) return undefined;
    return new VendorTeamDeparture(VendorTeamDepartureSchema.deserialize(json));
  }

  /**
   * Every departure in `jsonArray` that this build can read, in the order the API sent them.
   *
   * A record it cannot read is dropped rather than failing the whole agreement: the roster is the
   * screen's subject and a departure is a note on it, so one unreadable note must not cost the reader
   * the team.
   */
  public static fromJSONArray(jsonArray: unknown[] | null | undefined): VendorTeamDeparture[] {
    if (isNullOrUndefined(jsonArray)) return [];
    const departures: VendorTeamDeparture[] = [];
    for (const json of jsonArray) {
      if (!VendorTeamDepartureSchema.isValidJSON(json)) continue;
      const departure = VendorTeamDeparture.fromJSON(json);
      if (departure) departures.push(departure);
    }
    return departures;
  }

  public readonly userId: Guid;
  public readonly legalEntityId: Guid;
  public readonly reason: VendorTeamDepartureReason;
  public readonly departedDate: AHDate;

  /** The words a screen puts beside this person, and the answer to "why are they not on the team?". */
  public get status(): string {
    return VENDOR_TEAM_DEPARTURE_STATUS[this.reason];
  }

  /** The short form of `status`, for a cell too narrow for the whole wording. */
  public get label(): string {
    return VENDOR_TEAM_DEPARTURE_LABEL[this.reason];
  }

  /** True when this person ended their own participation rather than being taken off by somebody else. */
  public get isSelfInitiated(): boolean {
    return this.reason === VendorTeamDepartureReason.Left;
  }

  public constructor(params: VendorTeamDepartureParams) {
    const validated = VendorTeamDepartureSchema.validate(params);
    this.userId = validated.userId;
    this.legalEntityId = validated.legalEntityId;
    this.reason = validated.reason;
    this.departedDate = validated.departedDate;
  }

  public toJSON(): VendorTeamDepartureJSON {
    return VendorTeamDepartureSchema.serialize(this);
  }

  public toString(): string {
    return `${this.status} (userId: ${this.userId})`;
  }

  public isEqualTo(other: VendorTeamDeparture | null | undefined): boolean {
    if (!other) return false;
    return (
      this.userId.isEqualTo(other.userId) &&
      this.legalEntityId.isEqualTo(other.legalEntityId) &&
      this.reason === other.reason &&
      this.departedDate.isSame(other.departedDate)
    );
  }

  public clone(): VendorTeamDeparture {
    return new VendorTeamDeparture({
      userId: this.userId.clone(),
      legalEntityId: this.legalEntityId.clone(),
      reason: this.reason,
      departedDate: this.departedDate.clone(),
    });
  }
}
